import React, { useEffect, useState } from 'react';
import { Upload, Pencil, Loader2, IdCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { getCvPassport, getCvPublicProfile, type CvPassport, type CvPublicProfile } from '../../api/passport';
import PassportCover from './PassportCover';

interface PassportPreviewCardProps {
  cvId: string | null;
  /** Open the full Passport tab (edit/manage). */
  onOpen: () => void;
}

/**
 * Compact Passport preview for the Profile page: shows exactly the anonymized
 * cover explorers see, plus an "Edit" action that jumps to the Passport tab.
 * Falls back to an upload nudge when there's no CV yet.
 */
const PassportPreviewCard: React.FC<PassportPreviewCardProps> = ({ cvId, onOpen }) => {
  const { t } = useTranslation(['passport', 'profile']);
  const { apiCall } = useAuth();

  const [passport, setPassport] = useState<CvPassport | null>(null);
  const [realTalk, setRealTalk] = useState<CvPublicProfile | null>(null);
  const [thumb, setThumb] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    let cancelled = false;
    if (!cvId) return;
    setLoading(true);
    (async () => {
      const [p, rt, imgRes] = await Promise.all([
        getCvPassport(apiCall, cvId).catch(() => null),
        getCvPublicProfile(apiCall, cvId).catch(() => null),
        apiCall(`${apiBase}/api/v1/cv/${cvId}/images`).then(r => (r.ok ? r.json() : null)).catch(() => null),
      ]);
      if (cancelled) return;
      setPassport(p);
      setRealTalk(rt);
      setThumb(imgRes?.images?.[0] ?? null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cvId]);

  if (!cvId) {
    return (
      <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <IdCard className="w-4 h-4 text-pink-500" />
          <h3 className="text-sm sm:text-base font-semibold text-slate-800">{t('passport:title')}</h3>
        </div>
        <p className="text-xs sm:text-sm text-slate-600 mb-4 leading-relaxed">{t('passport:states.noCvSubtitle')}</p>
        <button
          onClick={onOpen}
          className="inline-flex items-center gap-1.5 sm:gap-2 px-3.5 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold rounded-lg hover:from-pink-600 hover:to-rose-600 transition-all shadow-sm"
        >
          <Upload className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          {t('passport:states.noCvCta')}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <IdCard className="w-4 h-4 text-pink-500" />
          <h3 className="text-sm sm:text-base font-semibold text-slate-800">{t('passport:title')}</h3>
        </div>
        <button
          onClick={onOpen}
          className="flex items-center gap-1 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5 text-pink-600 hover:text-pink-700 hover:bg-pink-100 rounded-lg transition-colors flex-shrink-0"
        >
          <Pencil className="w-3.5 h-3.5" />
          {t('passport:common.edit')}
        </button>
      </div>

      {loading && !passport ? (
        <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> …
        </div>
      ) : (
        <PassportCover
          thumbnail={thumb}
          realTalkSentence={realTalk?.real_talk_sentence ?? null}
          tags={realTalk?.real_talk_tags ?? null}
          stations={passport?.stations ?? null}
          loading={loading}
          onClick={onOpen}
        />
      )}
    </div>
  );
};

export default PassportPreviewCard;
